import axios from 'axios';

// Opts one request out of the global 404 toast below, for screens that
// render their own not-found state.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipGlobalNotFound?: boolean;
  }
}

const BASE_URL = import.meta.env.VITE_API_URL;

// Overridable per-environment for testing (e.g. set
// VITE_API_TIMEOUT_MS=3000 locally to simulate a dead connection
// without waiting on the browser's own connect timeout).
const DEFAULT_TIMEOUT_MS = 30_000;
const TIMEOUT_MS = import.meta.env.VITE_API_TIMEOUT_MS
  ? Number(import.meta.env.VITE_API_TIMEOUT_MS)
  : DEFAULT_TIMEOUT_MS;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

let errorHandler:
  | ((error: {
      title: string;
      message: string;
      type: 'error';
    }) => void)
  | null = null;

export const setGlobalErrorHandler = (
  handler: typeof errorHandler,
) => {
  errorHandler = handler;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.response &&
      errorHandler &&
      error.response.status === 404 &&
      !error.config?.skipGlobalNotFound
    ) {
      const { data, config } = error.response;
      let errorMessage =
        'The requested resource could not be found. It may have been moved or deleted.';

      try {
        if (config?.responseType === 'blob' && data instanceof Blob) {
          const text = await data.text();
          const json = JSON.parse(text);
          errorMessage =
            json?.detail || json?.message || errorMessage;
        } else {
          errorMessage =
            data?.detail || data?.message || errorMessage;
        }
      } catch (parseError) {
        console.error(parseError);
      }

      errorHandler({
        title: 'Resource Not Found',
        message: errorMessage,
        type: 'error',
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
